import { useLayoutEffect, useRef } from "react";

/**
 * How much of the bottom of the screen the fixed furniture is using.
 *
 * The tab bar and the composer are both fixed over the kid surface, and three
 * separate places needed to know how tall they are: the composer sits above the
 * tab bar, the toast sits above the composer, and anything scrolled to the
 * bottom has to clear all of it.
 *
 * Those numbers used to be written down — `bottom-[92px]`, `bottom-[172px]`,
 * `164px` — measured by eye on an iPad. They were wrong on the first iPhone
 * anybody opened: the tab bar's labels wrapped onto two lines, which made it
 * taller than 92px, so the composer sat across the middle of it and clipped the
 * microphone. A hardcoded height is a guess about a font, a language and a
 * screen width, and it is wrong the first time any of the three changes.
 *
 * These publish the real rendered heights as CSS variables instead, so the
 * layout follows the furniture rather than a note about what it used to
 * measure.
 */
function publish(name: string, element: HTMLElement | null): () => void {
  if (!element || typeof ResizeObserver === "undefined") return () => {};

  const write = () => {
    // `offsetHeight` rather than the rect: it is the laid-out height, so it does
    // not change mid-animation the way a transformed bounding box does.
    document.documentElement.style.setProperty(name, `${element.offsetHeight}px`);
  };

  write();
  const observer = new ResizeObserver(write);
  observer.observe(element);
  return () => {
    observer.disconnect();
    document.documentElement.style.removeProperty(name);
  };
}

/**
 * Attach to a fixed bottom element to publish its height as `name`.
 *
 * `useLayoutEffect` so the variable exists before the browser paints — with
 * `useEffect` the composer is briefly positioned against the fallback and jumps
 * once on every mount.
 */
export function usePublishedHeight<T extends HTMLElement>(name: string) {
  const ref = useRef<T>(null);
  useLayoutEffect(() => publish(name, ref.current), [name]);
  return ref;
}
